/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import FormHelperText from "@mui/material/FormHelperText";

import GridSectionHeader from "../../../widgets/GridSectionHeader";
import GridDropdown from "../../../widgets/GridDropdown";
import GridCheckbox from "../../../widgets/GridCheckbox";

import PageNavigator, { onNext } from "../../../PageNavigator";

import { alphanum } from "../../../../utils/fieldValidator.js";
import { MAX_LENGTH_TEXTAREA } from "../../../../utils/constants.js";
import { PRIORITIES } from "../../../../utils/constants.js";

import {
  updateObjectives,
  updateProtection,
  updateWealthAccumulation,
  updateWealthPreservation,
} from "../../../../states/slcObjectives.js";

// let haveMedicalConditionError = "";
let medConditionDescError = "";

let familyAndAssetsError = "";
let disabilityError = "";
let criticalIlnessError = "";
let longTermCareError = "";
let hospitalizationError = "";
let personalAccidentError = "";
let pregnancyError = "";
let dependantNeedsError = "";

let wealthPreservationDescError = "";

export default function Objectives() {
  const objectives = useSelector((state) => state.objectives);
  const protection = objectives.protection;
  const wealthAccumulation = objectives.wealthaccumulation;
  const wealthPreservation = objectives.wealthpreservation;

  //FORM VALIDATATION STATUS
  const [isValid, setIsValid] = useState(false);

  // HEALTH DECLARATION
  const [haveMedicalCondition, setHaveMedicalCondition] = useState(objectives.haveMedicalCondition);
  const [haveMedicalConditionError, setHaveMedicalConditionError] = useState("");
  const [medConditionDesc, setMedConditionDesc] = useState(objectives.medConditionDesc);
  const [isMedPolicyAcknowledged, setIsMedPolicyAcknowledged] = useState(objectives.isMedPolicyAcknowledged);
  const [isMedPolicyAcknowledgedError, setIsMedPolicyAcknowledgedError] = useState("");

  //PROTECTION
  const [familyAndAssets, setFamilyAndAssets] = useState(protection.familyAndAssets.priority);
  const [disability, setDisability] = useState(protection.disability.priority);
  const [criticalIlness, setCriticalIlness] = useState(protection.criticalIlness.priority);
  const [longTermCare, setLongTermCare] = useState(protection.longTermCare.priority);
  const [hospitalization, setHospitalization] = useState(protection.hospitalization.priority);
  const [personalAccident, setPersonalAccident] = useState(protection.personalAccident.priority);
  const [pregnancy, setPregnancy] = useState(protection.pregnancy.priority);
  const [dependantNeeds, setDependantNeeds] = useState(protection.dependantNeeds.priority);

  //WEALTH ACCUMULATION
  const [retirementPlanning, setRetirementPlanning] = useState(wealthAccumulation.retirementPlanning.priority);
  const [childrenEducation, setChildrenEducation] = useState(wealthAccumulation.childrenEducation.priority);
  const [otherPurpose, setOtherPurpose] = useState(wealthAccumulation.otherPurpose.priority);

  //WEALTH PRESERVATION
  const [srs, setSrs] = useState(wealthPreservation.srs.priority);
  const [estatePlanning, setEstatePlanning] = useState(wealthPreservation.estatePlanning.priority);
  const [wealthPreservationDesc, setWealthPreservationDesc] = useState(wealthPreservation.wealthPreservationDesc);

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  //CALLED WHEN "NEXT BUTTON" IS PRESSED. WHEN ATTEMPTING TO MOVE TO NEXT PAGE.
  function validateForm(e) {
    e.preventDefault();

    if (haveMedicalCondition.length === 0) {
      haveMedicalConditionError = "Please provide medical condition";
      return;
    }

    if (haveMedicalCondition === "YES" && medConditionDesc.length === 0) {
      setHaveMedicalConditionError("Please provide a reason");
      //haveMedicalConditionError = "Please provide a reason";
      return;
    }

    if (isMedPolicyAcknowledged === "") {
      setIsMedPolicyAcknowledgedError("Please acknowledge health policy");
      return;
    }

    if (
      haveMedicalCondition !== "" &&
      // isMedPolicyAcknowledged === true &&
      wealthPreservationDesc.length <= MAX_LENGTH_TEXTAREA
    ) {
      if (
        familyAndAssets.length > 0 ||
        disability.length > 0 ||
        criticalIlness.length > 0 ||
        longTermCare.length > 0 ||
        hospitalization.length > 0 ||
        personalAccident.length > 0 ||
        pregnancy.length > 0 ||
        dependantNeeds.length > 0 ||
        retirementPlanning.length > 0 ||
        childrenEducation.length > 0 ||
        otherPurpose.length > 0 ||
        srs.length > 0 ||
        estatePlanning.length > 0
      ) {
        setIsValid(true);
        onNext();
      } else {
        alert("Please select at least 1 financial objective.");
      }
    }
  }

  /**
   * 1. VALIDITE FORM USING ERROR MESSAGES
   *     "CALCULATE VALIDITY" IS SIMILAR TO "VALIDATE FORM" IT VALIDATES BY ERROR MESSAGE.
   *       WHEN ALL FIELD'S ERROR MESSAGES ARE EMPTY. (VALID = TRUE)
   *
   * 2. THIS FUNCTION IS ALSO RESPONSIBLE FOR ENABLING / DISABLING THE NEXT BUYTTON, THEREFORE IS TRIGGERED BY EACH UI EVENT
   */
  function calculateValidity() {
    if (haveMedicalConditionError.length === 0 && medConditionDescError.length === 0) {
      setIsValid(true);
    }
  }

  function handleChange(e, object) {
    if (e instanceof PointerEvent) {
      //DROPDOWNS
      id = object.props.id.toLowerCase();

      //PROTECTION
      if (id === "familyandassets") {
        const action = { type: "familyAndAssets", payload: e.target.value };
        setFamilyAndAssets(e.target.value);
        dispatch(updateProtection(action));
        familyAndAssetsError = "";
      }

      if (id === "disability") {
        const action = { type: "disability", payload: e.target.value };
        setDisability(e.target.value);
        dispatch(updateProtection(action));
        disabilityError = "";
      }

      if (id === "criticalilness") {
        const action = { type: "criticalIlness", payload: e.target.value };
        setCriticalIlness(e.target.value);
        dispatch(updateProtection(action));
        criticalIlnessError = "";
      }

      if (id === "longtermcare") {
        const action = {
          type: "longtermcare",
          payload: e.target.value,
        };
        setLongTermCare(e.target.value);
        dispatch(updateProtection(action));
        longTermCareError = "";
      }

      if (id === "hospitalization") {
        const action = { type: "hospitalization", payload: e.target.value };
        setHospitalization(e.target.value);
        dispatch(updateProtection(action));
        hospitalizationError = "";
      }

      if (id === "personalaccident") {
        const action = { type: "personalAccident", payload: e.target.value };
        setPersonalAccident(e.target.value);
        dispatch(updateProtection(action));
        personalAccidentError = "";
      }

      if (id === "pregnancy") {
        const action = {
          type: "pregnancy",
          payload: e.target.value,
        };
        dispatch(updateProtection(action));
        setPregnancy(e.target.value);
        pregnancyError = "";
      }

      if (id === "dependantneeds") {
        const action = { type: "dependantNeeds", payload: e.target.value };
        setDependantNeeds(e.target.value);
        dispatch(updateProtection(action));
        dependantNeedsError = "";
      }

      //WEALTH ACCUMULATION
      if (id === "retirementplanning") {
        const action = { type: "retirementPlanning", payload: e.target.value };
        dispatch(updateWealthAccumulation(action));
        setRetirementPlanning(e.target.value);
      }

      if (id === "childreneducation") {
        const action = { type: "childrenEducation", payload: e.target.value };
        dispatch(updateWealthAccumulation(action));
        setChildrenEducation(e.target.value);
      }

      if (id === "otherpurpose") {
        const action = { type: "otherPurpose", payload: e.target.value };
        dispatch(updateWealthAccumulation(action));
        setOtherPurpose(e.target.value);
      }

      //WEALTH PRESERVATION
      if (id === "srs") {
        const action = { type: "srs", payload: e.target.value };
        dispatch(updateWealthPreservation(action));
        setSrs(e.target.value);
      }

      if (id === "estateplanning") {
        const action = { type: "estateplanning", payload: e.target.value };
        dispatch(updateWealthPreservation(action));
        setEstatePlanning(e.target.value);
      }
    } else {
      //RADIO BUTTONS & TEXTFIELDS
      let type = "";
      let name = "";

      id = e.target.id;

      if (id === "") {
        type = e.target.type;
        name = e.target.name;
      }

      //MEDICAL CONDITION - ACKNOWLEDGE
      if (type === "radio" && name === "have-medical-condition") {
        setHaveMedicalCondition(e.target.value.toUpperCase());
        setHaveMedicalConditionError("");

        const action = {
          type: "haveMedicalCondition",
          payload: e.target.value.toUpperCase(),
        };
        dispatch(updateObjectives(action));
      }

      //MEDICAL CONDITION - ACKNOWLEDGE POLICY
      if (id === "isMedPolicyAcknowledged") {
        setIsMedPolicyAcknowledged(e.target.checked);
        setIsMedPolicyAcknowledgedError("");

        const action = {
          type: "isMedPolicyAcknowledged",
          payload: e.target.checked,
        };
        dispatch(updateObjectives(action));
      }

      //MEDICAL CONDITION - DESCRIPTION
      if (id === "medConditionDesc" && alphanum.test(e.target.value)) {
        if (e.target.value.length === 0) medConditionDescError = "Please provide a reason";
        else if (e.target.value.length > MAX_LENGTH_TEXTAREA) medConditionDescError = "Exceeded 1000 characters";
        else medConditionDescError = "";

        setMedConditionDesc(e.target.value);

        const action = {
          type: "medConditionDesc",
          payload: e.target.value,
        };
        dispatch(updateObjectives(action));
      }

      //WEALTH PRESERVATION
      if (id === "wealthPreservationDesc" && alphanum.test(e.target.value)) {
        if (e.target.value.length > MAX_LENGTH_TEXTAREA) wealthPreservationDescError = "Exceeded 1000 characters";
        else wealthPreservationDescError = "";

        const action = {
          type: "wealthPreservationDesc",
          payload: e.target.value,
        };
        dispatch(updateWealthPreservation(action));
      }

      setWealthPreservationDesc(e.target.value);
    }

    calculateValidity();
  }

  return (
    <>
      {/* <HealthDeclaration /> */}
      <Box component="form" onSubmit={validateForm} noValidate>
        <Grid container spacing={2} sx={{ pb: 2 }}>
          <Grid size={12}>
            <GridSectionHeader
              sectionName="Health Declaration"
              sx={{
                p: 1,
                pl: 1.8,
                mt: 0,
                mb: 1,
                fontSize: 18,
                fontWeight: "bold",
                color: "#FFFFFF", //WHITE
                backgroundColor: "#10B981",
              }}
            />

            <Typography variant="body1" sx={{ pt: 2 }}>
              Do you or any applicants have any medical condition that requires regular attention from the clinic or
              hospital?
            </Typography>

            <RadioGroup
              row
              name="have-medical-condition"
              defaultValue=""
              value={haveMedicalCondition}
              onChange={handleChange}
            >
              <FormControlLabel id="medicalConditionYes" value="YES" control={<Radio />} label="Yes" />
              <FormControlLabel id="medicalConditionNo" value="NO" control={<Radio />} label="No" />
            </RadioGroup>

            {haveMedicalCondition === "YES" ? (
              <>
                <Grid size={12}>
                  <Typography variant="body1">Please specify medical condition.</Typography>

                  <TextareaAutosize
                    id="medConditionDesc"
                    placeholder="Type here..."
                    style={{ width: "100%" }}
                    minRows={5}
                    onChange={handleChange}
                  />

                  {haveMedicalConditionError && (
                    <FormHelperText error={haveMedicalConditionError}>{haveMedicalConditionError}</FormHelperText>
                  )}
                </Grid>
              </>
            ) : (
              ""
            )}

            <Typography
              variant="h6"
              gutterBottom
              sx={{ p: 2, my: 1, border: "1px solid", fontWeight: "bold", borderRadius: 5 }}
            >
              Please Acknowledge Before Proceeding:
              <GridCheckbox
                id="isMedPolicyAcknowledged"
                key="isMedPolicyAcknowledged"
                columnSize="12"
                label="Applicants with pre-existing conditions may be subject to further health underwriting and undergo further health checkups/medical tests. You have also been made aware that subject to underwriting outcomes, your pre-existing conditions may not be covered, or your policy may be imposed with additional conditions, or your application may be rejected.I acknowledge that I have been identified a Selected Client"
                checked={isMedPolicyAcknowledged}
                onChange={handleChange}
                error={isMedPolicyAcknowledgedError}
              />
            </Typography>
            {isMedPolicyAcknowledgedError && (
              <FormHelperText error={isMedPolicyAcknowledgedError}>{isMedPolicyAcknowledgedError}</FormHelperText>
            )}
          </Grid>
        </Grid>

        {/* <Protection /> */}
        <Grid container spacing={2} sx={{ pb: 2 }}>
          {/* PROTECTION */}
          <GridSectionHeader
            sectionName="Protection"
            sx={{
              p: 1,
              pl: 1.8,
              mt: 0,
              mb: 1,
              fontSize: 18,
              fontWeight: "bold",
              color: "#FFFFFF", //WHITE
              backgroundColor: "#10B981",
            }}
          />

          <GridDropdown
            id="familyAndAssets"
            key="familyAndAssets"
            columnSize="6"
            label="Family/Assets in the event of Death"
            defaultValue=""
            value=""
            currState={familyAndAssets}
            onChange={handleChange}
            data={PRIORITIES}
            error={familyAndAssetsError}
          />

          <GridDropdown
            id="disability"
            key="disability"
            columnSize="6"
            label="Disability"
            defaultValue=""
            value=""
            currState={disability}
            onChange={handleChange}
            data={PRIORITIES}
            error={disabilityError}
          />

          <GridDropdown
            id="criticalIlness"
            key="criticalIlness"
            columnSize="6"
            label="Critical Illness"
            defaultValue=""
            value=""
            currState={criticalIlness}
            onChange={handleChange}
            data={PRIORITIES}
            error={criticalIlnessError}
          />

          <GridDropdown
            id="longTermCare"
            key="longTermCare"
            columnSize="6"
            label="Long Term Care/Disability Income"
            defaultValue=""
            value=""
            currState={longTermCare}
            onChange={handleChange}
            data={PRIORITIES}
            error={longTermCareError}
          />

          <GridDropdown
            id="hospitalization"
            key="hospitalization"
            columnSize="6"
            label="Hospital, Surgical, Medical Cost"
            defaultValue=""
            value=""
            currState={hospitalization}
            onChange={handleChange}
            data={PRIORITIES}
            error={hospitalizationError}
          />

          <GridDropdown
            id="personalAccident"
            key="personalAccident"
            columnSize="6"
            label="Personal Accident"
            defaultValue=""
            value=""
            currState={personalAccident}
            onChange={handleChange}
            data={PRIORITIES}
            error={personalAccidentError}
          />

          <GridDropdown
            id="pregnancy"
            key="pregnancy"
            columnSize="6"
            label="Pregnancy Complications / Death"
            defaultValue=""
            value=""
            currState={pregnancy}
            onChange={handleChange}
            data={PRIORITIES}
            error={pregnancyError}
          />

          <GridDropdown
            id="dependantNeeds"
            key="dependantNeeds"
            columnSize="6"
            label="Dependant Needs"
            defaultValue=""
            value=""
            currState={dependantNeeds}
            onChange={handleChange}
            data={PRIORITIES}
            error={dependantNeedsError}
          />
        </Grid>
        {/* <WealthAccumulation /> */}
        <Grid container spacing={2} sx={{ pb: 2 }}>
          {/* PROTECTION */}
          <GridSectionHeader
            sectionName="Wealth Accumulation"
            sx={{
              p: 1,
              pl: 1.8,
              mt: 0,
              mb: 1,
              fontSize: 18,
              fontWeight: "bold",
              color: "#FFFFFF", //WHITE
              backgroundColor: "#10B981",
            }}
          />

          <GridDropdown
            id="retirementPlanning"
            key="retirementPlanning"
            columnSize="6"
            label="Planning for Retirement"
            defaultValue=""
            value=""
            currState={retirementPlanning}
            onChange={handleChange}
            data={PRIORITIES}
          />

          <GridDropdown
            id="childrenEducation"
            key="childrenEducation"
            columnSize="6"
            label="Children's Education"
            defaultValue=""
            value=""
            currState={childrenEducation}
            onChange={handleChange}
            data={PRIORITIES}
          />

          <GridDropdown
            id="otherPurpose"
            key="otherPurpose"
            columnSize="6"
            label="Other Purposes"
            defaultValue=""
            value=""
            currState={otherPurpose}
            onChange={handleChange}
            data={PRIORITIES}
          />
        </Grid>
        {/* <WealthPreservation /> */}
        <Grid container spacing={2} sx={{ pb: 2 }}>
          <GridSectionHeader
            sectionName="Wealth Preservation"
            sx={{
              p: 1,
              pl: 1.8,
              mt: 0,
              mb: 1,
              fontSize: 18,
              fontWeight: "bold",
              color: "#FFFFFF", //WHITE
              backgroundColor: "#10B981",
            }}
          />

          <Typography variant="body1" gutterBottom>
            SRS and Estate planning are financial planning tools. If SRS and/or Estate planning is/are selected, please
            select at least one (1) of the Financial Objectives in "Protection" or Wealth Accumulation" where you wish
            to apply for the financial planning tool(s).
          </Typography>

          <GridDropdown
            id="srs"
            key="srs"
            columnSize="6"
            label="Supplementary Retirement Scheme (SRS) Planning"
            defaultValue=""
            value=""
            currState={srs}
            onChange={handleChange}
            data={PRIORITIES}
          />

          <GridDropdown
            id="estatePlanning"
            key="estatePlanning"
            columnSize="6"
            label="Estate Planning (Eg. Will, Trust, CPF Nomination, Power of Attorney)"
            defaultValue=""
            value=""
            currState={estatePlanning}
            onChange={handleChange}
            data={PRIORITIES}
          />

          <Grid size={12}>
            <Typography variant="body1">Additional Notes </Typography>

            <TextareaAutosize
              id="wealthPreservationDesc"
              placeholder="Type here..."
              style={{ width: "100%" }}
              minRows={5}
              onChange={handleChange}
              defaultValue={wealthPreservationDesc}
            />

            {wealthPreservationDescError && (
              <FormHelperText error={wealthPreservationDescError}>{wealthPreservationDescError}</FormHelperText>
            )}
          </Grid>
        </Grid>

        <PageNavigator validator={validateForm} isValid={isValid} />
      </Box>
    </>
  );
}
